import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { RUPEE_SYMBOL } from '../../constants'

interface AmountInputProps {
  value: string
  cursorPos: number
  onCursorChange: (pos: number) => void
  inputRef?: React.RefObject<HTMLDivElement>
  placeholder?: string
  enableCursorCustomTouch?: boolean
  initialFontSize?: number
  enableFontSizeAdjustment?: boolean
  disabledCursor?: boolean
}

// Optimized Indian number formatting - much faster than regex-based approach
const formatIndianNumberFast = (num: string): string => {
  if (!num || num === '0') return ''

  // Remove existing commas
  const cleanNum = num.replace(/,/g, '')
  if (!/^\d+$/.test(cleanNum)) return cleanNum

  const len = cleanNum.length
  if (len <= 3) return cleanNum

  let result = ''
  let i = len

  // Process from right to left, adding commas every 2 digits after the first 3
  while (i > 0) {
    if (i === len) {
      // Last 3 digits
      result = cleanNum.substring(Math.max(0, i - 3), i) + result
      i -= 3
    } else {
      // Next 2 digits
      const start = Math.max(0, i - 2)
      result = cleanNum.substring(start, i) + ',' + result
      i -= 2
    }
  }

  return result
}

// Optimized cursor style with blink animation
const cursorStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '3px',
  height: '0.9em',
  background: '#2E8AE5',
  marginLeft: '3px',
  marginRight: '-1px',
  verticalAlign: 'middle',
  animation: 'blink-cursor 1s steps(1) infinite'
}

// Add optimized CSS animation to prevent lag
if (!document.getElementById('amount-input-cursor-style')) {
  const styleSheet = document.createElement('style')
  styleSheet.id = 'amount-input-cursor-style'
  styleSheet.innerHTML = `
@keyframes blink-cursor {
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
}`
  document.head.appendChild(styleSheet)
}

// Helper function to create test element
const createTestElement = (displayValue: string, fontSize: number) => {
  const testSpan = document.createElement('span')
  Object.assign(testSpan.style, {
    fontWeight: '500',
    visibility: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    fontSize: `${fontSize}px`,
    top: '-9999px',
    left: '-9999px'
  })
  testSpan.textContent = displayValue
  document.body.appendChild(testSpan)
  return testSpan
}

// Optimized font size adjustment logic
const adjustFontSizeToFit = (
  container: HTMLElement,
  displayValue: string,
  initialFontSize: number,
  onFontSizeChange: (size: number) => void
) => {
  const containerWidth = container.offsetWidth - 40

  // Early return if no content
  if (!displayValue.trim()) {
    onFontSizeChange(initialFontSize)
    return
  }

  const testSpan = createTestElement(displayValue, initialFontSize)

  try {
    // Quick check if it already fits at initial font size
    if (testSpan.offsetWidth <= containerWidth) {
      onFontSizeChange(initialFontSize)
      return
    }

    // Binary search for optimal font size (only when overflowing)
    let minSize = 12
    let maxSize = initialFontSize
    let bestFit = initialFontSize

    while (minSize <= maxSize) {
      const midSize = Math.floor((minSize + maxSize) / 2)
      testSpan.style.fontSize = `${midSize}px`

      if (testSpan.offsetWidth <= containerWidth) {
        bestFit = midSize
        minSize = midSize + 1
      } else {
        maxSize = midSize - 1
      }
    }

    onFontSizeChange(bestFit)
  } finally {
    document.body.removeChild(testSpan)
  }
}

const CustomAmountInput: React.FC<AmountInputProps> = ({
  value,
  cursorPos,
  onCursorChange,
  inputRef,
  placeholder = 'invest',
  enableCursorCustomTouch = false,
  initialFontSize = 48,
  enableFontSizeAdjustment = true,
  disabledCursor = false
}) => {
  const containerRef = inputRef || useRef<HTMLDivElement>(null)
  const [fontSize, setFontSize] = useState(initialFontSize)
  const adjustFontSizeTimeoutRef = useRef<NodeJS.Timeout>()

  // Use optimized formatting function
  const formattedValue = useMemo(() => formatIndianNumberFast(value), [value])

  // Simplified cursor positioning - no complex mapping needed
  const displayValue = formattedValue || placeholder
  const showCursor = !value || (value && cursorPos >= 0)

  // Optimized font size adjustment - only when there's overflow
  const adjustFontSize = useCallback(() => {
    if (!containerRef.current || !enableFontSizeAdjustment) return

    // Clear existing timeout
    if (adjustFontSizeTimeoutRef.current) {
      clearTimeout(adjustFontSizeTimeoutRef.current)
    }

    // Debounce the font size adjustment
    adjustFontSizeTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current) return

      // Quick overflow check before expensive calculation
      const container = containerRef.current
      const containerWidth = container.offsetWidth - 40

      // Use shared helper to create test element
      const quickTest = createTestElement(displayValue, initialFontSize)
      const hasOverflow = quickTest.offsetWidth > containerWidth
      document.body.removeChild(quickTest)

      // Only run expensive font size adjustment if there's overflow
      if (hasOverflow) {
        adjustFontSizeToFit(container, displayValue, initialFontSize, setFontSize)
      } else {
        // No overflow, use initial font size
        setFontSize(initialFontSize)
      }
    }, 100) // 100ms debounce
  }, [displayValue, initialFontSize, enableFontSizeAdjustment])

  // Adjust font size when value changes (debounced)
  useEffect(() => {
    adjustFontSize()

    // Cleanup timeout on unmount
    return () => {
      if (adjustFontSizeTimeoutRef.current) {
        clearTimeout(adjustFontSizeTimeoutRef.current)
      }
    }
  }, [adjustFontSize])

  // Simplified click handler
  const handleInputClick = useCallback(() => {
    if (!enableCursorCustomTouch || !value) return

    // Simple approach: place cursor at end for now
    // This can be enhanced later if needed
    onCursorChange(value.length)
  }, [value, onCursorChange, enableCursorCustomTouch])

  return (
    <div className='flex items-center relative w-full'>
      <span
        className='text-[44px] font-medium text-[#232323] mr-[1px] select-none'
        style={{ fontSize: `${fontSize}px` }}
      >
        {RUPEE_SYMBOL}
      </span>
      <div className='relative flex-1 overflow-x-hidden overflow-y-visible'>
        <div
          ref={containerRef}
          className='font-medium outline-none bg-transparent px-2 mb-[2px] w-full flex items-center cursor-text relative'
          style={{
            minWidth: '120px',
            textAlign: 'left',
            position: 'relative',
            background: 'transparent',
            zIndex: 2,
            paddingRight: '28px',
            fontSize: `${fontSize}px`,
            overflow: 'hidden'
          }}
          tabIndex={0}
          aria-label='Enter amount'
          onClick={handleInputClick}
        >
          {!value ? (
            <>
              <div className='!-ml-1' style={cursorStyle} />
              <span
                className='pointer-events-none select-none font-medium text-[#E0E0E0]'
                style={{ fontSize: `${fontSize}px` }}
              >
                {placeholder}
              </span>
            </>
          ) : (
            <>
              <span className='text-[#232323]' style={{ fontSize: `${fontSize}px` }}>
                {displayValue}
              </span>
              {!disabledCursor && showCursor && <div style={cursorStyle} />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomAmountInput
